import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanDeactivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PasConfirmationService } from '@porsche-aftersales/frontend-toolbox';

import { RecourseBookingTabsetComponent } from '../../content/recourse-booking/recourse-booking-tabset.component';
import { RecourseBookingUrlConstants } from '../../content/recourse-booking/recourse-booking-url-constants';
import { checkBookingRights, checkReadRights } from '../../services/booking/recourse-booking-functions';
import { RecourseBookingStore } from '../../services/booking/recourse-booking-store';
import { BookingRoutingService } from '../../services/details-view-service/booking-routing.service';
import { UserService } from '../../services/user-service/user.service';

import { confirmNavbarTabChangeDialog } from './guard-functions';

@Injectable({
  providedIn: 'root',
})
export class RecourseBookingGuard {
  private readonly translateService = inject(TranslateService);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly recourseBookingStore = inject(RecourseBookingStore);
  private readonly location = inject(Location);
  private readonly bookingRoutingService = inject(BookingRoutingService);

  public async canDeactivate(
    target: RecourseBookingTabsetComponent,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): Promise<boolean> {
    const isTabChangeEvent: boolean = nextState.url.startsWith(`/${RecourseBookingUrlConstants.MAIN_PATH}`);

    if (
      nextState.url ===
      `/${RecourseBookingUrlConstants.MAIN_PATH}/${RecourseBookingUrlConstants.OVERVIEW_TAB}/${this.recourseBookingStore.selectedRecourseBookingId}`
    ) {
      return true;
    }

    const nextUrlIsRecourseBooking = nextState.url === `/${RecourseBookingUrlConstants.MAIN_PATH}`;

    if (target.addEditDetails?.formChanged) {
      if (
        !nextUrlIsRecourseBooking &&
        (await confirmNavbarTabChangeDialog(this.translateService, this.pasConfirmationService)) === true
      ) {
        return true;
      } else {
        if (isTabChangeEvent) {
          const currentTab: string = this.extractTabId(currentState.url);
          target.openTab(currentTab);
        }
        const currentUrlTree = this.router.createUrlTree([], currentRoute);
        const currentUrl = currentUrlTree.toString();
        this.location.go(currentUrl);
        return false;
      }
    } else {
      return true;
    }
  }

  private extractTabId(url: string): string {
    return url.split('/')[2];
  }

  public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return this.properUserRights() && this.validUrl(route);
  }

  private properUserRights(): boolean {
    return checkReadRights(this.userService.getRights());
  }

  /**
   * Urls that are valid:
   * /recourseBooking/overview; /recourseBooking/details/:id; /recourseBooking/edit/:id; /recourseBooking/new;
   * @param route current route(url)
   * @returns true if urls are valid and details are successfully loaded, otherwise false
   */
  /* eslint-disable complexity */
  private async validUrl(route: ActivatedRouteSnapshot): Promise<boolean> {
    const tabId: string = route.params.tabId;
    const bookingId: number = route.params.id;
    const validOverviewUrl: boolean = !bookingId && tabId === RecourseBookingUrlConstants.OVERVIEW_TAB;
    const validDetailsUrl: boolean = bookingId && tabId === RecourseBookingUrlConstants.DETAILS_TAB;
    const validEditUrl: boolean = bookingId && tabId === RecourseBookingUrlConstants.EDIT_TAB;
    const validNewUrl: boolean =
      !bookingId && tabId === RecourseBookingUrlConstants.NEW_TAB && checkBookingRights(this.userService.getRights());
    if (validDetailsUrl || validEditUrl) {
      // if booking needs to be loaded for edit, it's checked in BE is it editable
      const bookingDetailsLoadedSuccess: boolean = await this.recourseBookingStore.getRecourseBookingDetails(
        bookingId,
        tabId === RecourseBookingUrlConstants.EDIT_TAB,
      );
      if (!bookingDetailsLoadedSuccess) {
        this.redirectToDefaultLocation();
        return false;
      }
    }
    const validBookingsUrl: boolean = validOverviewUrl || validDetailsUrl || validEditUrl || validNewUrl;
    if (!validBookingsUrl) {
      this.redirectToDefaultLocation();
    }
    return validBookingsUrl;
  }
  /* eslint-enable */

  private redirectToDefaultLocation(): void {
    this.bookingRoutingService.openBookingsOverview();
  }
}

export const recourseBookingGuardCanActivate: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean> =>
  inject(RecourseBookingGuard).canActivate(route);

export const recourseBookingGuardCanDeactivate: CanDeactivateFn<RecourseBookingTabsetComponent> = async (
  target: RecourseBookingTabsetComponent,
  currentRoute: ActivatedRouteSnapshot,
  currentState: RouterStateSnapshot,
  nextState?: RouterStateSnapshot,
): Promise<boolean> => inject(RecourseBookingGuard).canDeactivate(target, currentRoute, currentState, nextState);
